import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteFreeSoloValueMapping, AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

interface ExternalVendorOption {
    code?: string;
    name?: string;
    phone?: string;
}

interface ExternalVendorAutocompleteProps<T = ExternalVendorOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false> extends Omit<AutocompleteProps<T, M, DC, FS>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    transformRecord?: (record: ExternalVendorOption) => T;

    [key: string]: any;
}

interface VendorSearchResponse {
    data?: {
        resultList?: ExternalVendorOption[];
    };
    resultFlag?: boolean;
}

/**
 * Autocomplete for Spire/external vendors via Vendors/Search.
 * Value identity and import key is vendor `code`.
 */
const ExternalVendorAutocomplete = <T = ExternalVendorOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false>({
                                                                                                                                                     textFieldProps = {},
                                                                                                                                                     form = false,
                                                                                                                                                     name = undefined,
                                                                                                                                                     required = false,
                                                                                                                                                     setLoadingOptions: setLoadingOptionsProps,
                                                                                                                                                     transformRecord,
                                                                                                                                                     ...props
                                                                                                                                                 }: ExternalVendorAutocompleteProps<T, M, DC, FS>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component.
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches external vendor options from /Vendors/Search.
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        const forApi = {
            paginationInfo: {currentPage: 1, pageSize: 20},
            filters: keywords ? {keyword: keywords} : {},
        };
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.searchVendors(forApi, cancelToken) as VendorSearchResponse;
            if (!isMounted())
                return [];
            return response?.data?.resultList?.map(e => (transformRecord?.(e) ?? e) as T) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted, transformRecord]);

    /**
     * Determines if two vendor options are equal based on their codes
     */
    const isOptionEqualToValue = useCallback((option: ExternalVendorOption, value: ExternalVendorOption | null) => option?.code === value?.code, []);

    /**
     * Gets the display label for a vendor option (vendor code)
     */
    const getOptionLabel = useCallback((option: ExternalVendorOption | AutocompleteFreeSoloValueMapping<FS>) =>
        props.freeSolo === true ? option : (option as ExternalVendorOption)?.code ?? "", [props.freeSolo]);

    /**
     * Gets the unique key for a vendor option (vendor code)
     */
    const getOptionKey = useCallback((option: ExternalVendorOption | AutocompleteFreeSoloValueMapping<FS>) =>
        props.freeSolo === true ? option : (option as ExternalVendorOption)?.code ?? "", [props.freeSolo]);

    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<T, M, DC, FS>
            keywordsBased
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue as (option: T, value: T | null) => boolean}
            getOptionLabel={getOptionLabel as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            getOptionKey={getOptionKey as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default ExternalVendorAutocomplete;
